// services/creditService.js
import { supabase } from '../data/supabase';

export const creditService = {
  // Verificar pacotes expirados
  async checkExpiredPackages(patientId) {
    try {
      const now = new Date().toISOString();
      
      // Buscar pacotes expirados
      const { data: expiredPackages, error } = await supabase
        .from('packages')
        .select('*')
        .eq('patient_id', patientId)
        .eq('status', 'active')
        .lt('package_expiry', now);
      
      if (error) throw error;
      
      if (!expiredPackages || expiredPackages.length === 0) {
        return { success: true, message: 'Nenhum pacote expirado' };
      }
      
      // Atualizar status dos pacotes expirados
      for (const pkg of expiredPackages) {
        // Atualizar status do pacote
        const { error: updateError } = await supabase
          .from('packages')
          .update({ status: 'expired' })
          .eq('id', pkg.id);
        
        if (updateError) throw updateError;
        
        // Buscar a carteira correspondente
        const { data: wallets, error: walletError } = await supabase
          .from('wallet')
          .select('*')
          .eq('patient_id', patientId)
          .eq('type', 'package')
          .eq('session_type', pkg.type); // Usar session_type em vez de credit_type
        
        if (walletError) throw walletError;
        
        if (wallets && wallets.length > 0) {
          // Registrar transação de expiração
          const { error: txError } = await supabase
            .from('credit_transactions')
            .insert({
              wallet_id: wallets[0].id,
              amount: -pkg.remaining_sessions,
              transaction_type: 'expiration',
              source_type: 'package',
              source_id: pkg.id,
              created_at: now,
              notes: `Expiração automática de pacote ${pkg.type} com ${pkg.remaining_sessions} sessões restantes`
            });
          
          if (txError) throw txError;
        }
      }
      
      return { 
        success: true, 
        message: `${expiredPackages.length} pacotes expirados processados` 
      };
    } catch (error) {
      console.error('Erro ao verificar pacotes expirados:', error);
      return { success: false, error };
    }
  },
  
  // Obter carteira do paciente
  async getPatientWallet(patientId) {
    try {
      // Buscar carteiras do paciente
      const { data: wallets, error: walletsError } = await supabase
        .from('wallet')
        .select('*')
        .eq('patient_id', patientId);
      
      if (walletsError) throw walletsError;
      
      if (!wallets || wallets.length === 0) {
        return [];
      }
      
      // Buscar transações para todas as carteiras
      const { data: allTransactions, error: txError } = await supabase
        .from('credit_transactions')
        .select(`
          *,
          wallet:wallet_id(*)
        `)
        .in('wallet_id', wallets.map(w => w.id));
      
      if (txError) throw txError;
      
      // Processar créditos por tipo de sessão
      const creditsByType = {};
      
      // Inicializar tipos padrão
      ['individual', 'casal', 'familia'].forEach(type => {
        creditsByType[type] = {
          type,
          availableCredits: 0,
          totalCredits: 0,
          usedCredits: 0
        };
      });
      
      // Processar transações
      if (allTransactions) {
        allTransactions.forEach(tx => {
          const wallet = wallets.find(w => w.id === tx.wallet_id);
          if (!wallet) return;
          
          const sessionType = wallet.session_type || 'individual';
          
          if (!creditsByType[sessionType]) {
            creditsByType[sessionType] = {
              type: sessionType,
              availableCredits: 0,
              totalCredits: 0,
              usedCredits: 0
            };
          }
          
          if (tx.amount > 0) {
            creditsByType[sessionType].totalCredits += tx.amount;
          } else {
            creditsByType[sessionType].usedCredits += -tx.amount;
          }
        });
      }
      
      // Calcular créditos disponíveis
      Object.values(creditsByType).forEach(credit => {
        credit.availableCredits = credit.totalCredits - credit.usedCredits;
      });
      
      // Retornar apenas tipos com créditos
      return Object.values(creditsByType).filter(credit => 
        credit.totalCredits > 0 || credit.availableCredits > 0
      );
    } catch (error) {
      console.error('Erro ao buscar carteira do paciente:', error);
      throw error;
    }
  },
  
  // Usar crédito para agendamento
  async useCredit(appointmentId, walletId, sessionType) {
    try {
      // Buscar detalhes do agendamento
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointment')
        .select('*')
        .eq('id', appointmentId)
        .single();
      
      if (appointmentError) throw appointmentError;
      
      // Buscar carteira
      const { data: wallet, error: walletError } = await supabase
        .from('wallet')
        .select('*')
        .eq('id', walletId)
        .single();
      
      if (walletError) throw walletError;
      
      // Verificar saldo
      const { data: transactions, error: txError } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('wallet_id', walletId);
      
      if (txError) throw txError;
      
      const totalCredits = transactions.reduce((sum, tx) => 
        sum + (tx.amount > 0 ? tx.amount : 0), 0);
      
      const usedCredits = transactions.reduce((sum, tx) => 
        sum + (tx.amount < 0 ? -tx.amount : 0), 0);
      
      const availableCredits = totalCredits - usedCredits;
      
      if (availableCredits < 1) {
        throw new Error('Saldo insuficiente');
      }
      
      // Registrar uso de crédito
      const { data: creditTx, error: creditTxError } = await supabase
        .from('credit_transactions')
        .insert({
          wallet_id: walletId,
          amount: -1,
          transaction_type: 'appointment',
          source_type: wallet.type === 'package' ? 'package' : 'individual_credit',
          appointment_id: appointmentId,
          created_at: new Date().toISOString(),
          notes: `Agendamento de sessão ${sessionType}`
        })
        .select()
        .single();
      
      if (creditTxError) throw creditTxError;
      
      return {
        success: true,
        transaction: creditTx
      };
    } catch (error) {
      console.error('Erro ao usar crédito:', error);
      throw error;
    }
  }
};